// frontend/src/components/stats/ClipButton.jsx
//
// 2026-10-22 (per Gilles) — LISTEN to the tape at one moment: a goal, a
// flagged call, any heard event. One tap fetches a short clip (10 s before
// the call to 60 s after) and plays it right there, on the computer or the
// phone; tapping again closes it. The clip is built on the server from the
// stored recording and never saved anywhere.
import { useEffect, useRef, useState } from 'react';
import { Volume2, Loader2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api.js';
import Tip from '../Tip.jsx';

const mmss = (s) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;

export default function ClipButton({ teamId, recordingId, offsetMs, label = null }) {
  const [state, setState] = useState('idle');    // idle | loading | open
  const [url, setUrl] = useState(null);
  const urlRef = useRef(null);
  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);
  if (!recordingId || offsetMs == null) return null;

  const open = async () => {
    if (state === 'open') { setState('idle'); return; }
    setState('loading');
    try {
      const { data } = await api.get(`/teams/${teamId}/press-box/recordings/${recordingId}/clip`,
        { params: { at: Math.round(offsetMs) }, responseType: 'blob' });
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = URL.createObjectURL(data);
      setUrl(urlRef.current);
      setState('open');
    } catch {
      toast.error('Could not load the audio for this moment');
      setState('idle');
    }
  };

  // the call sits this many seconds INTO the clip (clip starts 10 s before
  // the call, or at 0:00 when the call came in the first 10 s of the tape)
  const callAtS = Math.min(10, Math.max(0, offsetMs / 1000));
  return (
    <span className="inline-flex items-center gap-1" style={{ verticalAlign: 'middle', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
      <Tip text={`Listen to the tape at this moment. The clip starts 10 seconds before the call (the call itself is at ${mmss(callAtS)} of the clip) and runs up to a minute after, so you can hear the context and judge the call yourself.`}>
        <button type="button" data-clip-button
          style={{ padding: '3px 9px', borderRadius: 999, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
            background: state === 'open' ? 'var(--ice)' : 'var(--ice-a8)',
            color: state === 'open' ? '#fff' : 'var(--ice)',
            border: '1px solid var(--ice)', fontWeight: 700, fontSize: 11 }}
          onClick={open} aria-label="Listen to this moment of the tape">
          {state === 'loading' ? <Loader2 size={13} className="animate-spin" /> : state === 'open' ? <X size={13} /> : <Volume2 size={13} />}
          {label && <span className="text-xs">{label}</span>}
        </button>
      </Tip>
      {state === 'open' && url && (
        <>
          <audio controls autoPlay src={url} style={{ height: 28, maxWidth: 230 }} />
          <span className="text-xs" style={{ color: 'var(--ice)', fontFamily: 'var(--font-mono)' }}
            title="Where the call sits inside this clip">call at {mmss(callAtS)}</span>
        </>
      )}
    </span>
  );
}
